import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Copy, Loader2, RefreshCw } from 'lucide-react';
import { Alert, EmptyState, PageHeader } from '../ui';
import { ordersService } from '../../services/orders.service';
import { orderErrorMessage } from './orderError';
import { CANCELLABLE_STATUSES, instrumentName, sideLabel } from './orderPresentation';
import OrderDetailView from './OrderDetailView';

const OrderDetailPage = () => {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState(null);

  const load = () => {
    if (!orderId) return;
    setLoading(true);
    setError(null);
    Promise.resolve(ordersService.getOrder(orderId))
      .then((result) => {
        if (!result?.id) {
          setOrder(null);
          setError('This order could not be found.');
          return;
        }
        setOrder(result);
      })
      .catch((err) => {
        setOrder(null);
        setError(orderErrorMessage(err, 'This order could not be loaded.'));
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // Reload when the route id changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const title = order
    ? [sideLabel(order.side), order.symbol].filter(Boolean).join(' ') || 'Order'
    : 'Order';
  const instrument = order ? instrumentName(order) : null;

  const canCancel = order && CANCELLABLE_STATUSES.has(order.status);

  const handleCancel = async () => {
    if (!order?.id) return;
    setCancelError(null);
    setCancelling(true);
    try {
      await ordersService.cancelOrder(order.id);
      load();
    } catch (err) {
      setCancelError(orderErrorMessage(err, 'The order could not be cancelled.'));
    } finally {
      setCancelling(false);
    }
  };

  const copyId = () => {
    if (!order?.id) return;
    navigator.clipboard.writeText(order.id).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <div className="max-w-5xl">
      <PageHeader
        title={loading ? 'Order' : title}
        description={instrument || undefined}
        breadcrumb={['Orders', loading ? '…' : (order?.symbol || 'Order')]}
        actions={(
          <div className="flex items-center gap-2">
            <Link to="/orders" className="btn btn-sm btn-secondary">
              <ArrowLeft className="h-3.5 w-3.5" />
              All orders
            </Link>
            {order?.id && (
              <button type="button" onClick={copyId} className="btn btn-sm btn-secondary">
                <Copy className="h-3.5 w-3.5" />
                {copied ? 'Copied' : 'Copy ID'}
              </button>
            )}
            <button type="button" onClick={load} className="btn btn-sm btn-secondary" aria-label="Refresh order">
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            </button>
            {canCancel && (
              <button
                type="button"
                onClick={handleCancel}
                disabled={cancelling}
                className="btn btn-sm btn-danger disabled:opacity-50"
              >
                {cancelling && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                Cancel order
              </button>
            )}
          </div>
        )}
      />

      {cancelError && (
        <div className="mb-4">
          <Alert tone="danger">{cancelError}</Alert>
        </div>
      )}

      {loading ? (
        <div className="bg-white border border-ink-200 rounded-xl shadow-card flex items-center justify-center py-16 gap-2 text-ink-500">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span className="text-sm">Loading order…</span>
        </div>
      ) : error || !order ? (
        <div className="bg-white border border-ink-200 rounded-xl shadow-card">
          <EmptyState title="Order unavailable" description={error || 'This order could not be found.'} />
        </div>
      ) : (
        <OrderDetailView order={order} variant="page" showCancel={false} />
      )}
    </div>
  );
};

export default OrderDetailPage;
